import React from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { EsgDeleteIcon, EsgEditIcon } from "assets";
import { COLORS } from "components";
import type { EmissionTarget, EmissionTargetLevel } from "api/emission-targets";
import { TARGET_TYPES } from "../add-target-modal/types";

const LEVEL_TO_UI: Record<EmissionTargetLevel, string> = {
  GLOBAL: "Corporate",
  BUSINESS_UNIT: "Business Unit",
  SITE: "Site",
};

const LEVEL_CHIP_STYLES: Record<
  EmissionTargetLevel,
  { bg: string; border: string; color: string }
> = {
  GLOBAL: {
    bg: "#F2F5F7",
    border: "#2F3E46",
    color: "#2F3E46",
  },
  BUSINESS_UNIT: {
    bg: "#F3E5B5",
    border: "#8A5A00",
    color: "#8A5A00",
  },
  SITE: {
    bg: "#D9E6E1",
    border: "#2E7D32",
    color: "#2E7D32",
  },
};

const TARGET_TYPE_TO_UI: Record<string, string> = {
  ABSOLUTE: TARGET_TYPES.ABSOLUTE_TCO2E,
  PERCENTAGE: TARGET_TYPES.PERCENT_REDUCTION,
  PERCENT_REDUCTION: TARGET_TYPES.PERCENT_REDUCTION,
};

const ANNUAL_PATH_TO_UI: Record<string, string> = {
  EVEN: "Even reduction",
  CUSTOM: "Custom annual values",
};

/** startsWith that safely handles null/undefined cell values. */
function safeStartsWith(
  row: { getValue: (id: string) => unknown },
  id: string,
  filterValue: unknown
): boolean {
  const f = String(filterValue ?? "").toLowerCase().trim();
  if (!f) return true;
  const val = row.getValue(id);
  if (val == null) return false;
  return String(val).toLowerCase().trim().startsWith(f);
}

const LevelChip = ({ level }: { level: EmissionTargetLevel }) => {
  const styles = LEVEL_CHIP_STYLES[level];
  const label = LEVEL_TO_UI[level];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        px: 1.5,
        py: 0.5,
        borderRadius: "9999px",
        border: "1px solid",
        backgroundColor: styles.bg,
        borderColor: styles.border,
        color: styles.color,
        fontSize: "12px",
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
};

export function getTargetsTableColumns(
  onEdit: (target: EmissionTarget) => void,
  onDeleteClick: (target: EmissionTarget) => void
): MRT_ColumnDef<EmissionTarget>[] {
  return [
    {
      accessorKey: "level",
      header: "Level",
      Cell: ({ row }) => (
        <LevelChip level={row.original.level} />
      ),
      filterFn: (row, id, filterValue) => {
        const f = String(filterValue ?? "").toLowerCase().trim();
        if (!f) return true;
        const level = row.getValue(id) as EmissionTargetLevel | null | undefined;
        if (level == null) return false;
        const label = LEVEL_TO_UI[level];
        return label.toLowerCase().trim().startsWith(f);
      },
      size: 120,
      minSize: 90,
    },
    {
      accessorKey: "entity",
      header: "Entity",
      Cell: ({ cell }) => cell.getValue<string>() ?? "—",
      filterFn: safeStartsWith,
      size: 120,
      minSize: 70,
    },
    {
      accessorKey: "targetType",
      header: "Target Type",
      Cell: ({ cell }) =>
        TARGET_TYPE_TO_UI[cell.getValue<string>() ?? ""] ?? cell.getValue<string>() ?? "—",
      filterFn: safeStartsWith,
      size: 140,
      minSize: 110,
    },
    {
      accessorKey: "scope",
      header: "Emissions Scope",
      Cell: ({ cell }) => cell.getValue<string>() ?? "—",
      filterFn: safeStartsWith,
      size: 140,
      minSize: 110,
    },
    {
      accessorKey: "baseYear",
      header: "Baseline Year",
      Cell: ({ cell }) => cell.getValue<number>() ?? "—",
      filterFn: safeStartsWith,
      size: 120,
      minSize: 100,
    },
    {
      accessorKey: "targetYear",
      header: "Target Year",
      Cell: ({ cell }) => cell.getValue<number>() ?? "—",
      filterFn: safeStartsWith,
      size: 120,
      minSize: 90,
    },
    {
      accessorKey: "value",
      header: "Target Value",
      Cell: ({ row, cell }) => {
        const value = cell.getValue<number>();
        if (value == null) return "—";
        const type = row.original.targetType;
        return type === "PERCENTAGE"
          ? `${value}%`
          : `${value} tCO₂e`;
      },
      filterFn: safeStartsWith,
      size: 120,
      minSize: 95,
    },
    {
      accessorKey: "annual_target_path",
      header: "Annual Target Path",
      Cell: ({ cell }) =>
        ANNUAL_PATH_TO_UI[cell.getValue<string>() ?? ""] ?? "—",
      filterFn: safeStartsWith,
      size: 160,
      minSize: 140,
    },
    {
      accessorKey: "notes",
      header: "Notes",
      filterFn: safeStartsWith,
      Cell: ({ cell }) => {
        const value = cell.getValue<string>();
        if (value == null || value === "") return "—";
        return (
          <Box
            sx={{
              maxWidth: 160,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={value}
          >
            {value}
          </Box>
        );
      },
      size: 160,
      minSize: 70,
    },
    {
      id: "actions",
      header: "Actions",
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 0, alignItems: "center" }}>
          <Tooltip title="Edit">
            <IconButton
              aria-label="Edit target"
              size="small"
              sx={{ width: 36, height: 36, padding: "4px" }}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(row.original);
              }}
            >
              <EsgEditIcon color={COLORS.Romance} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              aria-label="Delete target"
              size="small"
              sx={{ width: 36, height: 36, padding: "4px" }}
              onClick={(e) => {
                e.stopPropagation();
                onDeleteClick(row.original);
              }}
            >
              <EsgDeleteIcon color="#EF2E14" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      size: 100,
      minSize: 90,
      enableSorting: false,
    },
  ];
}
